<script setup lang="ts">
import useModel from '../model/model'
import { ref } from 'vue'
import { ObjectSearch, WCoordsMap, WCoordsMapButton } from '@/widgets'
import { useHelpers } from '@/shared/lib'
import { useSendForm } from './../model/api'
import { VIcons } from '@/shared/ui'
import { AdminCommandsSendSchedule } from '@/features/Cabinet'
import { ObjectTypeIds } from '@/entities/Object'
import { useAuth } from '@/entities/Auth'

const model = useModel()
const { timeZones, numberList } = useHelpers()
const { sendForm, isPending } = useSendForm()
const { isPermissions } = useAuth()

const valid = ref(false)

/**
 * Функция-обработчик смены координат
 * @param latitude
 * @param longitude
 */
const setCoordsHandler = ({ latitude, longitude }: { latitude: number; longitude: number }) => {
  model.data.latitude = latitude
  model.data.longitude = longitude
}

const { isFetching } = model.loadData()

/**
 * Функция отправки формы
 */
const submitHandler = () => {
  sendForm(model.data, model.origin, valid.value).then(model.updateOrigin)
}
</script>

<template>
  <VForm
    v-model="valid"
    class="cabinet-main-form"
    :disabled="isFetching"
    @submit.prevent="submitHandler"
  >
    <div class="form__grid_3">
      <VTextField :label="$t('itf.name')" v-model="model.data.name" :rules="model.rules.name" />

      <ObjectSearch
        :label="$t('itf.objects_group')"
        v-model="model.data.parent_id"
        :object_type="ObjectTypeIds.GROUPS"
        :disabled="!isPermissions(['OBJECT_MOVE'])"
      />

      <VTextField :label="$t('itf.serial_number')" v-model="model.data.serial_number" readonly />
    </div>

    <div class="form__grid_3">
      <VTextField :label="$t('itf.soft_version')" v-model="model.data.soft_version" readonly />

      <div class="form__btn_right">
        <VSelect :label="$t('itf.time_zone')" v-model="model.data.time_zone" :items="timeZones" />

        <VTooltip :text="$t('itf.summer_time')" open-delay="500" location="top">
          <template #activator="{ props }">
            <VCheckbox true-icon="sunny" v-bind="props" v-model="model.data.summer_time" />
          </template>
        </VTooltip>
      </div>

      <div class="form__btn_right">
        <VSelect
          :label="$t('itf.schedule')"
          :items="model.scheduleList"
          v-model="model.data.schedule_id"
        />

        <AdminCommandsSendSchedule>
          <VBtn :class="{ disabled: !isPermissions(['DEVICE_CONTROL']) }">
            <VIcons type="event_upcoming" />
          </VBtn>
        </AdminCommandsSendSchedule>
      </div>
    </div>

    <div class="form__grid_3">
      <VSelect
        :label="$t('itf.contactors')"
        v-model="model.data.contactors_count"
        :items="numberList(9)"
      />

      <VSelect
        :label="$t('itf.protect')"
        v-model="model.data.protects_count"
        :items="numberList(24)"
      />

      <VTextField :label="$t('itf.address')" v-model="model.data.address" />
    </div>

    <div class="form__grid_3">
      <VTextField :label="$t('itf.latitude')" v-model="model.data.latitude">
        <template #append-inner>
          <WCoordsMapButton />
        </template>
      </VTextField>

      <VTextField :label="$t('itf.longitude')" v-model="model.data.longitude">
        <template #append-inner>
          <WCoordsMapButton />
        </template>
      </VTextField>
    </div>

    <div>
      <VBtn type="submit" :loading="isPending">{{ $t('itf.save') }}</VBtn>
    </div>
  </VForm>

  <WCoordsMap
    :latitude="model.data.latitude"
    :longitude="model.data.longitude"
    @setCoords="setCoordsHandler"
  />
</template>
